/**
 * Side-by-side comparison of a book's ORIGINAL and CORRECTED versions, so the
 * admin/handler can spot the writer's changes during approval. Each pane is a
 * full in-app reader (PDF/DOCX/EPUB) with its own remembered reading position.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import BookReader from '../../components/BookReader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookCompare = () => {
  const { bookId } = useParams();
  const [loading, setLoading] = useState(true);
  const [book, setBook] = useState(null);
  const [original, setOriginal] = useState(null);
  const [corrected, setCorrected] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [bookRes, origRes, corrRes] = await Promise.allSettled([
          axios.get(`${API}/proofreading/work/${bookId}`),
          axios.get(`${API}/proofreading/reader-info/${bookId}?which=original`),
          axios.get(`${API}/proofreading/reader-info/${bookId}?which=corrected`),
        ]);
        if (bookRes.status === 'fulfilled' && bookRes.value.data?.id) setBook(bookRes.value.data);
        if (origRes.status === 'fulfilled' && origRes.value.data?.url) {
          setOriginal({ url: `${BACKEND_URL}${origRes.value.data.url}`, mediaType: origRes.value.data.media_type });
        }
        if (corrRes.status === 'fulfilled' && corrRes.value.data?.url) {
          setCorrected({ url: `${BACKEND_URL}${corrRes.value.data.url}`, mediaType: corrRes.value.data.media_type });
        }
      } catch (e) {
        toast.error('Kon nie weergawes laai nie');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [bookId]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="book-compare-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/admin/books">
          <Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-2" /> Terug na Boeke Bestuur</Button>
        </Link>
        <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Vergelyk Weergawes{book ? `: ${book.boek_naam}` : ''}
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-2" data-testid="compare-original">
          <div className="px-3 py-2 rounded bg-slate-100 border text-sm font-semibold text-slate-700">
            Oorspronklike weergawe
          </div>
          {original ? (
            <BookReader bookFileUrl={original.url} mediaType={original.mediaType} bookmarkKey={`${bookId}-original`} />
          ) : (
            <div className="flex items-center gap-2 h-96 border rounded bg-gray-50 text-gray-500 justify-center">
              <AlertTriangle className="h-5 w-5" /> Geen oorspronklike weergawe beskikbaar nie.
            </div>
          )}
        </div>

        <div className="space-y-2" data-testid="compare-corrected">
          <div className="px-3 py-2 rounded bg-green-100 border border-green-300 text-sm font-semibold text-green-800">
            Gekorrigeerde weergawe
          </div>
          {corrected ? (
            <BookReader bookFileUrl={corrected.url} mediaType={corrected.mediaType} bookmarkKey={`${bookId}-corrected`} />
          ) : (
            <div className="flex items-center gap-2 h-96 border rounded bg-gray-50 text-gray-500 justify-center">
              <AlertTriangle className="h-5 w-5" /> Nog geen gekorrigeerde weergawe opgelaai nie.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookCompare;
